import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Plane,
  ArrowRight,
  AlertTriangle,
  Info,
  CheckCircle2,
  Ban,
  RotateCcw,
  Code2,
  ChevronDown,
  Layers,
  ExternalLink,
  Users,
  Ticket,
  Wallet,
  Luggage,
} from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { useGetImport, useIgnoreImport, useRestoreImport, useLookupImportBaggage } from "../../../api/booking/useImportApi";
import { importToPrefill, mergedTripOf, directionTotalsOf } from "./importToPrefill";
import s from "./ImportDetailDrawer.module.css";

const money = (n, currency = "EUR") => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const formatted = Number(n)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, "#")
    .replace(".", ",")
    .replace(/#/g, ".");
  return `${currency} ${formatted}`;
};

/**
 * Dates arrive as plain `YYYY-MM-DD` strings with no timezone. Formatting them
 * through `new Date()` on a UTC server or a browser west of GMT shifts them a
 * day — the same bug already fixed once in the document formatters — so they
 * are split textually here too.
 */
const formatDay = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};
const shortDay = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-");
  return m && d ? `${d}/${m}${y ? `/${y}` : ""}` : "";
};

const TYPE_LABEL = { ADT: "Adult", CHD: "Child", INF: "Infant" };

/* ── One flight leg, laid out like the itinerary cards elsewhere in Bookings ── */
const LegCard = ({ leg }) => (
  <div className={s.legCard}>
    <div className={s.legDirection}>
      <span className={leg.direction === "Inbound" ? s.dirInbound : s.dirOutbound}>{leg.direction || "—"}</span>
      <span className={s.legFlight}>
        {leg.airlineCode}
        {leg.flightNumber}
      </span>
      {leg.flightStatus && <span className={s.legStatus}>{leg.flightStatus}</span>}
    </div>

    <div className={s.legRoute}>
      <div className={s.legPoint}>
        <div className={s.legAirport}>{leg.departure || "—"}</div>
        <div className={s.legTime}>{leg.time || "—"}</div>
        <div className={s.legDate}>{shortDay(leg.date)}</div>
      </div>

      <div className={s.legLine}>
        <span className={s.legLineTrack} />
        <Plane size={13} className={s.legPlane} />
      </div>

      <div className={`${s.legPoint} ${s.legPointRight}`}>
        <div className={s.legAirport}>{leg.arrival || "—"}</div>
        <div className={s.legTime}>{leg.arrivalTime || "—"}</div>
        <div className={s.legDate}>{shortDay(leg.arrivalDate)}</div>
      </div>
    </div>

    <div className={s.legMeta}>
      {leg.cabinClass && <span>{leg.cabinClass}</span>}
      {/* The fare Airtuerk named, when it named one. Otherwise the booking class
          letter the file carries, labelled as the class it is rather than
          passed off as a fare. */}
      {leg.fareClassName ? (
        <span>{leg.fareClassName}</span>
      ) : (
        leg.fareClass && <span>Class {leg.fareClass}</span>
      )}
      {leg.baggageChecked && (leg.baggageCheckedKg > 0 || leg.baggageCheckedPc > 0) && (
        <span>
          Checked {leg.baggageCheckedKg > 0 ? `${leg.baggageCheckedKg} kg` : `${leg.baggageCheckedPc} pc`}
        </span>
      )}
      {leg.baggageCabin && (leg.baggageCabinKg > 0 || leg.baggageCabinPc > 0) && (
        <span>Cabin {leg.baggageCabinKg > 0 ? `${leg.baggageCabinKg} kg` : `${leg.baggageCabinPc} pc`}</span>
      )}
      {leg.baggageCheckedInfant && (leg.baggageCheckedInfantKg > 0 || leg.baggageCheckedInfantPc > 0) && (
        <span>
          Infant {leg.baggageCheckedInfantKg > 0 ? `${leg.baggageCheckedInfantKg} kg` : `${leg.baggageCheckedInfantPc} pc`}
        </span>
      )}
      {!leg.baggageChecked && !leg.baggageCabin && <span className={s.legNoBaggage}>No baggage allowance</span>}
      {leg.pnr && <span className={s.legPnr}>{leg.pnr}</span>}
    </div>
  </div>
);

const ImportDetailDrawer = ({ importId, onClose, onChanged, onOpenRelated }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data, loading, error, refetch } = useGetImport(importId);
  const { execute: ignoreImport, loading: ignoring } = useIgnoreImport();
  const { execute: restoreImport, loading: restoring } = useRestoreImport();
  const { execute: lookupBaggage, loading: lookingUpBaggage } = useLookupImportBaggage();

  // The file never states a baggage allowance; Airtuerk's fare data does. This
  // runs on ingest, so the button is for rows that landed before it existed or
  // while Airtuerk was down — and it also writes onto the booking, if any.
  const handleLookupBaggage = async () => {
    const res = await lookupBaggage(data.id);
    if (res.success) {
      showToast(res.message || "Baggage allowance looked up on Airtuerk", "success");
      onChanged?.();
      refetch();
    } else {
      showToast(res.message || "Baggage lookup failed", "error");
    }
  };
  const [showXml, setShowXml] = useState(false);

  // A fresh row should never inherit the previous one's expanded raw XML.
  useEffect(() => {
    setShowXml(false);
  }, [importId]);

  // Escape closes, which is the one shortcut people try in a drawer.
  useEffect(() => {
    if (!importId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [importId, onClose]);

  // The whole trip, not just the file that was opened. A multicheck return
  // arrives as two records and each looks one-way alone, so showing only this
  // one hid the inbound leg and half the money. Same merge the wizard uses.
  const parsed = useMemo(() => mergedTripOf(data), [data]);
  // Split tickets are two purchases; show what each direction actually cost.
  const directionTotals = useMemo(() => directionTotalsOf(parsed), [parsed]);
  const blockers = useMemo(() => (data?.warnings || []).filter((w) => w.severity === "blocker"), [data]);
  const notes = useMemo(() => (data?.warnings || []).filter((w) => w.severity !== "blocker"), [data]);

  const canCreate = data && !["IMPORTED", "SUPERSEDED", "FAILED"].includes(data.status);

  const handleCreate = () => {
    const prefill = importToPrefill(data);
    if (!prefill) {
      showToast("This import has nothing to prefill — the file could not be parsed", "error");
      return;
    }
    showToast("Opening Add Booking with the imported details", "success");
    navigate("/bookings/add", { state: { prefill } });
  };

  const handleIgnore = async () => {
    const res = await ignoreImport(data.id);
    if (res.success) {
      showToast("Import ignored", "success");
      onChanged?.();
      refetch();
    } else {
      showToast(res.message || "Failed to ignore import", "error");
    }
  };

  const handleRestore = async () => {
    const res = await restoreImport(data.id);
    if (res.success) {
      showToast("Import restored to the queue", "success");
      onChanged?.();
      refetch();
    } else {
      showToast(res.message || "Failed to restore import", "error");
    }
  };

  if (!importId) return null;

  return (
    <>
      <div className={s.backdrop} onClick={onClose} />
      <aside className={s.drawer} role="dialog" aria-label="Imported booking">
        {/* ── Header ── */}
        <header className={s.header}>
          <div className={s.headerMain}>
            <div className={s.headerTitleRow}>
              <span className={`${s.sourceChip} ${data?.source === "AER" ? s.sourceAer : s.sourceMc}`}>
                {data?.source === "AER" ? "Cockpit (AER)" : "Multicheck"}
              </span>
              <h2 className={s.title}>{data?.locator || "Unparsed file"}</h2>
              {data?.version > 0 && <span className={s.version}>version {data.version}</span>}
            </div>
            <p className={s.subtitle}>
              {data?.fileName || "—"}
              {data?.externalRef ? ` · ${data.externalRef}` : ""}
            </p>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {loading && (
          <div className={s.body}>
            <div className={s.skeletonBlock} />
            <div className={s.skeletonBlock} />
            <div className={s.skeletonBlock} />
          </div>
        )}

        {!loading && error && (
          <div className={s.body}>
            <div className={s.errorBox}>{error}</div>
          </div>
        )}

        {!loading && data && (
          <div className={s.body}>
            {/* ── Review checklist — first, because it decides what happens next ── */}
            <section className={s.section}>
              <h3 className={s.sectionTitle}>Review checklist</h3>

              {blockers.length === 0 && notes.length === 0 && (
                <div className={`${s.check} ${s.checkOk}`}>
                  <CheckCircle2 size={15} />
                  <div>
                    <div className={s.checkText}>Nothing missing — this can be booked as it stands.</div>
                  </div>
                </div>
              )}

              {blockers.map((w) => (
                <div key={w.code} className={`${s.check} ${s.checkBlocker}`}>
                  <AlertTriangle size={15} />
                  <div>
                    <div className={s.checkText}>{w.message}</div>
                    <div className={s.checkCode}>{w.code.replace(/_/g, " ").toLowerCase()}</div>
                  </div>
                </div>
              ))}

              {notes.map((w) => (
                <div key={w.code} className={`${s.check} ${s.checkInfo}`}>
                  <Info size={15} />
                  <div>
                    <div className={s.checkText}>{w.message}</div>
                  </div>
                </div>
              ))}

              {data.related?.length > 0 && (
                <div className={`${s.check} ${s.checkRelated}`}>
                  <Layers size={15} />
                  <div>
                    <div className={s.checkText}>
                      Airtuerk sent this trip as {data.related.length + 1} files sharing one supplier
                      reference. All of them are already included below — the itinerary, the
                      travellers and the total cover the whole trip, and creating the booking uses
                      every file. The other {data.related.length === 1 ? "file is" : "files are"}:
                    </div>
                    <div className={s.relatedList}>
                      {data.related.map((r) => (
                        <button key={r.id} className={s.relatedRow} onClick={() => onOpenRelated?.(r.id)}>
                          <span className={s.relatedRoute}>{r.route || r.fileName}</span>
                          <span className={s.relatedMeta}>
                            {r.travelDate || "—"} · {r.sell !== null ? money(r.sell, r.currency) : "—"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {data.status === "SUPERSEDED" && (
                <div className={`${s.check} ${s.checkInfo}`}>
                  <Info size={15} />
                  <div className={s.checkText}>
                    A newer version of this PNR has arrived — work from that one instead.
                  </div>
                </div>
              )}

              {data.booking && (
                <div className={`${s.check} ${s.checkOk}`}>
                  <CheckCircle2 size={15} />
                  <div className={s.checkText}>
                    Already booked as{" "}
                    <button className={s.inlineLink} onClick={() => navigate(`/bookings/${data.booking.id}`)}>
                      {data.booking.bookingReference}
                      <ExternalLink size={11} />
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* ── Money ── */}
            {parsed?.pricing && (
              <section className={s.section}>
                <h3 className={s.sectionTitle}>
                  <Wallet size={13} /> Pricing
                </h3>
                <div className={s.priceGrid}>
                  <div className={s.priceBox}>
                    <span className={s.priceLabel}>Purchase</span>
                    <span className={parsed.pricing.cost === null ? s.priceMissing : s.priceValue}>
                      {money(parsed.pricing.cost, parsed.currency) || "not in file"}
                    </span>
                  </div>
                  <div className={s.priceBox}>
                    <span className={s.priceLabel}>Selling</span>
                    <span className={parsed.pricing.sell === null ? s.priceMissing : s.priceValueStrong}>
                      {money(parsed.pricing.sell, parsed.currency) || "not in file"}
                    </span>
                  </div>
                  <div className={s.priceBox}>
                    <span className={s.priceLabel}>Margin</span>
                    <span className={parsed.pricing.margin === null ? s.priceMissing : s.priceValue}>
                      {money(parsed.pricing.margin, parsed.currency) || "unknown"}
                    </span>
                  </div>
                </div>
                {directionTotals && (
                  <div className={s.directionSplit}>
                    <div className={s.directionSplitHead}>
                      Ticketed separately — each direction has its own price
                    </div>
                    {["Outbound", "Inbound"].map((dir) =>
                      directionTotals[dir] === undefined ? null : (
                        <div key={dir} className={s.directionRow}>
                          <span className={s.directionName}>{dir}</span>
                          <span className={s.directionAmt}>
                            {money(directionTotals[dir], parsed.currency)}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                )}

                {parsed.pricing.perTraveler?.length > 0 && (
                  <div className={s.perTraveler}>
                    {parsed.pricing.perTraveler.map((p, i) => (
                      <div key={i} className={s.perRow}>
                        <span className={s.perName}>
                          {p.lastName}/{p.firstName}
                        </span>
                        <span className={s.perAmt}>
                          {money(p.fare, parsed.currency)}
                          {p.tax ? ` + ${money(p.tax, parsed.currency)} tax` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ── Itinerary ── */}
            {parsed?.flight?.legs?.length > 0 && (
              <section className={s.section}>
                <h3 className={s.sectionTitle}>
                  <Plane size={13} /> Itinerary
                  <span className={s.sectionMeta}>
                    {parsed.flight.tripType} · {parsed.flight.legs.length} leg
                    {parsed.flight.legs.length === 1 ? "" : "s"}
                  </span>
                </h3>
                <div className={s.legList}>
                  {parsed.flight.legs.map((leg, i) => (
                    <LegCard key={i} leg={leg} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Travellers ── */}
            {parsed?.travelers?.length > 0 && (
              <section className={s.section}>
                <h3 className={s.sectionTitle}>
                  <Users size={13} /> Travellers
                  <span className={s.sectionMeta}>{parsed.travelers.length}</span>
                </h3>
                <div className={s.travelerList}>
                  {parsed.travelers.map((t, i) => (
                    <div key={i} className={s.travelerRow}>
                      <span className={s.travelerType}>{TYPE_LABEL[t.type] || t.type}</span>
                      <span className={s.travelerName}>
                        {t.lastName}/{t.firstName}
                      </span>
                      <span className={t.dateOfBirth ? s.travelerMeta : s.travelerMissing}>
                        {t.dateOfBirth ? formatDay(t.dateOfBirth) : "no date of birth"}
                      </span>
                      <span className={t.gender ? s.travelerMeta : s.travelerMissing}>
                        {t.gender || "no gender"}
                      </span>
                      {t.ticketNumber && (
                        <span className={s.travelerTicket}>
                          <Ticket size={10} />
                          {t.ticketNumber}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Raw XML, collapsed. The only source of truth when a parse looks wrong. ── */}
            <section className={s.section}>
              <button className={s.xmlToggle} onClick={() => setShowXml((v) => !v)}>
                <Code2 size={13} />
                Raw XML
                <ChevronDown size={13} className={showXml ? s.chevronOpen : ""} />
              </button>
              {showXml && <pre className={s.xmlBlock}>{data.rawXml}</pre>}
            </section>

            {data.error && (
              <section className={s.section}>
                <h3 className={s.sectionTitle}>Parse error</h3>
                <div className={s.errorBox}>{data.error}</div>
              </section>
            )}
          </div>
        )}

        {/* ── Actions ── */}
        {!loading && data && (
          <footer className={s.footer}>
            {data.status === "IGNORED" ? (
              <button className={s.secondaryBtn} onClick={handleRestore} disabled={restoring}>
                <RotateCcw size={14} />
                {restoring ? "Restoring…" : "Restore to queue"}
              </button>
            ) : (
              <button className={s.secondaryBtn} onClick={handleIgnore} disabled={ignoring || data.status === "IMPORTED"}>
                <Ban size={14} />
                {ignoring ? "Ignoring…" : "Ignore"}
              </button>
            )}

            {data.parsed?.flight?.legs?.length > 0 && data.status !== "SUPERSEDED" && (
              <button
                className={s.secondaryBtn}
                onClick={handleLookupBaggage}
                disabled={lookingUpBaggage}
                title="Fetch the baggage allowance for these flights from Airtuerk's fare data"
              >
                <Luggage size={14} />
                {lookingUpBaggage ? "Looking up…" : "Look up baggage"}
              </button>
            )}

            <div className={s.footerRight}>
              {blockers.length > 0 && canCreate && (
                <span className={s.footerHint}>
                  {blockers.length} item{blockers.length === 1 ? "" : "s"} to complete in the wizard
                </span>
              )}
              <button className={s.primaryBtn} onClick={handleCreate} disabled={!canCreate}>
                Review &amp; create booking
                <ArrowRight size={15} />
              </button>
            </div>
          </footer>
        )}
      </aside>
    </>
  );
};

export default ImportDetailDrawer;
